import { CounterParty } from '../model/counter-party.model'
import { reserveBalanceFieldLabelFromKey } from '../model/draw-model'
import { InventoryType } from '../model/inventory-type.model'
import { Security } from '../model/security.model'
import { ExternalUnmatchedTransaction } from '../model/single-transaction/external-unmatched-transaction'
import { HistoricalBalanceData } from '../model/single-transaction/popup-input.model'
import { TradeEntryMatchRow } from '../model/single-transaction/trade/trade-entry-match.model'
import { TradeEntryModel } from '../model/single-transaction/trade/trade-entry.model'
import { Trade } from '../model/single-transaction/trade/trade-transaction.model'
import { TransactionMatch } from '../model/single-transaction/transaction-match'
import { TransactionTypeEnum } from '../model/transaction-type.model'
import { Calculators } from './calculators.util'
import { getCounterPartyFromName } from './common-utils'

export const tradeEntryRowNames = [
  'account',
  'transactionType',
  'dataSource',
  'entryDate',
  'settleDate',
  'postDate',
  'cusip',
  'cwanIdentifier',
  'price',
  'units',
  'commission',
  'originationFee',
  'exitFee',
  'exitFeeReceivable',
  'drawFee',
  'prepaymentPenaltyFee',
  'prepaidInterest',
  'interestDue',
  'interestBearingInterestReserve',
  'interestBearingPrincipalReserve',
  'nonInterestBearingInterestReserve',
  'nonInterestBearingPrincipalReserve',
  'decimalAccrued',
  'dollarAccrued',
  'factor',
  'priceScalar',
  'contractSize',
  'proceeds',
  'counterParty',
  'clearwaterCounterParty',
  'amortTargetDate',
  'amortTargetPrice',
  'inventoryType',
  'transTypeCode',
  'buyType',
  'drawType',
  'reserveBalanceField',
] as const

export type TradeEntryRowName = typeof tradeEntryRowNames[number]

/**
 * Function to Create Trade entry
 */
export function generateTradeEntry(
  reconDate: string,
): Record<TradeEntryRowName, TradeEntryModel> {
  return {
    account: {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    transactionType: {
      id: 'transactionType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: 'Buy',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    dataSource: {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    entryDate: {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: reconDate,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    settleDate: {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: reconDate,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    postDate: {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: reconDate,
      custody: '',
      trading: '',
      errorMessage: '',
    },
    cusip: {
      id: 'cusip',
      validityStatus: 'invalid',
      type: 'string',
      isEditable: true,
      field: 'Asset ID',
      value: '',
      custody: '',
      trading: '',
      errorMessage: 'No Security Selected',
    },
    cwanIdentifier: {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    price: {
      id: 'price',
      validityStatus: 'invalid',
      type: 'number',
      isEditable: true,
      field: 'Price',
      value: '',
      custody: '',
      trading: '',
      errorMessage: 'Price must be set',
    },
    units: {
      id: 'units',
      validityStatus: 'invalid',
      type: 'number',
      isEditable: true,
      field: 'Units',
      value: '',
      custody: '',
      trading: '',
      errorMessage: 'Units must be set',
    },
    commission: {
      id: 'commission',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Commission',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    originationFee: {
      id: 'originationFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Origination Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    exitFee: {
      id: 'exitFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Exit Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    exitFeeReceivable: {
      id: 'exitFeeReceivable',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Exit Fee Receivable',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    drawFee: {
      id: 'drawFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Draw Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    prepaymentPenaltyFee: {
      id: 'prepaymentPenaltyFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Prepayment Penalty Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    prepaidInterest: {
      id: 'prepaidInterest',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Prepaid Interest',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    interestDue: {
      id: 'interestDue',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Past Due Interest',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    interestBearingInterestReserve: {
      id: 'interestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    interestBearingPrincipalReserve: {
      id: 'interestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    nonInterestBearingInterestReserve: {
      id: 'nonInterestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Non-Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    nonInterestBearingPrincipalReserve: {
      id: 'nonInterestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Non-Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    decimalAccrued: {
      id: 'decimalAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Decimal Accrued',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    dollarAccrued: {
      id: 'dollarAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Dollar Accrued',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    factor: {
      id: 'factor',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Factor',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    priceScalar: {
      id: 'priceScalar',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Price Scalar',
      value: '1.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    contractSize: {
      id: 'contractSize',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Contract Size',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    proceeds: {
      id: 'proceeds',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Proceeds',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    counterParty: {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'External Counter Party',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    clearwaterCounterParty: {
      id: 'clearwaterCounterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Clearwater Counter Party',
      value: 'Unknown',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    amortTargetDate: {
      id: 'amortTargetDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Amort Target Date',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    amortTargetPrice: {
      id: 'amortTargetPrice',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Amort Target Price',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    inventoryType: {
      id: 'inventoryType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Inventory Type',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: 'LONG',
    },
    transTypeCode: {
      id: 'transTypeCode',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Code',
      value: 'BUY',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    buyType: {
      id: 'buyType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Buy Type',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    drawType: {
      id: 'drawType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Draw Type',
      value: 'Draw from Unfunded',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    reserveBalanceField: {
      id: 'reserveBalanceField',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Reserve Balance Field',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
  }
}

/**
 * Function to create Trade entry data from externalMatch
 */
export function createTradeEntryFromExternalTransactionCustody(
  externalTransaction: ExternalUnmatchedTransaction,
  accountName: string,
  isAggregate: boolean,
  hplMode: boolean,
): Record<TradeEntryRowName, TradeEntryModel> {
  return {
    account: {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: isAggregate,
      field: 'Account',
      value: accountName,
      custody: accountName,
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    transactionType: {
      id: 'transactionType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : 'Buy',
      custody: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    dataSource: {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      custody: externalTransaction.dataSource ?? '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    entryDate: {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: externalTransaction.entryDate,
      custody: externalTransaction.entryDate,
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    settleDate: {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: externalTransaction.settleDate,
      custody: externalTransaction.settleDate,
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    postDate: {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: externalTransaction.postDate,
      custody: externalTransaction.postDate,
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    cusip: {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Asset ID',
      value: externalTransaction.assetId ?? '',
      custody: externalTransaction.assetId ?? '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    cwanIdentifier: {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: externalTransaction.cwanIdentifier ?? '',
      custody: externalTransaction.cwanIdentifier ?? '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    price: {
      id: 'price',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Price',
      value: externalTransaction.price
        ? Number(externalTransaction.price).toString()
        : '',
      custody: externalTransaction.price
        ? Number(externalTransaction.price).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    units: {
      id: 'units',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Units',
      value: '',
      custody: externalTransaction.units
        ? Number(externalTransaction.units).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    commission: {
      id: 'commission',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Commission',
      value: externalTransaction.commission
        ? Number(externalTransaction.commission).toString()
        : '0.0',
      custody: externalTransaction.commission
        ? Number(externalTransaction.commission).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    originationFee: {
      id: 'originationFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Origination Fee',
      value: externalTransaction.originationFee
        ? Number(externalTransaction.originationFee).toString()
        : '0.0',
      custody: externalTransaction.originationFee
        ? Number(externalTransaction.originationFee).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFee: {
      id: 'exitFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee',
      value: externalTransaction.exitFee
        ? Number(externalTransaction.exitFee).toString()
        : '0.0',
      custody: externalTransaction.exitFee
        ? Number(externalTransaction.exitFee).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFeeReceivable: {
      id: 'exitFeeReceivable',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee Receivable',
      value: externalTransaction.exitFeeReceivable
        ? Number(externalTransaction.exitFeeReceivable).toString()
        : '0.0',
      custody: externalTransaction.exitFeeReceivable
        ? Number(externalTransaction.exitFeeReceivable).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawFee: {
      id: 'drawFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Draw Fee',
      value: externalTransaction.drawFee
        ? Number(externalTransaction.drawFee).toString()
        : '0.0',
      custody: externalTransaction.drawFee
        ? Number(externalTransaction.drawFee).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaymentPenaltyFee: {
      id: 'prepaymentPenaltyFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Prepayment Penalty Fee',
      value: externalTransaction.prepaymentPenaltyFee
        ? Number(externalTransaction.prepaymentPenaltyFee).toString()
        : '0.0',
      custody: externalTransaction.prepaymentPenaltyFee
        ? Number(externalTransaction.prepaymentPenaltyFee).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaidInterest: {
      id: 'prepaidInterest',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Prepaid Interest',
      value: externalTransaction.prepaidInterest
        ? Number(externalTransaction.prepaidInterest).toString()
        : '0.0',
      custody: externalTransaction.prepaidInterest
        ? Number(externalTransaction.prepaidInterest).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestDue: {
      id: 'interestDue',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Past Due Interest',
      value: externalTransaction.interestDue
        ? Number(externalTransaction.interestDue).toString()
        : '0.0',
      custody: externalTransaction.interestDue
        ? Number(externalTransaction.interestDue).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingInterestReserve: {
      id: 'interestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Interest Bearing)',
      value: externalTransaction.interestBearingInterestReserve
        ? Number(externalTransaction.interestBearingInterestReserve).toString()
        : '0.0',
      custody: externalTransaction.interestBearingInterestReserve
        ? Number(externalTransaction.interestBearingInterestReserve).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingPrincipalReserve: {
      id: 'interestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Interest Bearing)',
      value: externalTransaction.interestBearingPrincipalReserve
        ? Number(externalTransaction.interestBearingPrincipalReserve).toString()
        : '0.0',
      custody: externalTransaction.interestBearingPrincipalReserve
        ? Number(externalTransaction.interestBearingPrincipalReserve).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingInterestReserve: {
      id: 'nonInterestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Non-Interest Bearing)',
      value: externalTransaction.nonInterestBearingInterestReserve
        ? Number(
            externalTransaction.nonInterestBearingInterestReserve,
          ).toString()
        : '0.0',
      custody: externalTransaction.nonInterestBearingInterestReserve
        ? Number(
            externalTransaction.nonInterestBearingInterestReserve,
          ).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingPrincipalReserve: {
      id: 'nonInterestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Non-Interest Bearing)',
      value: externalTransaction.nonInterestBearingPrincipalReserve
        ? Number(
            externalTransaction.nonInterestBearingPrincipalReserve,
          ).toString()
        : '0.0',
      custody: externalTransaction.nonInterestBearingPrincipalReserve
        ? Number(
            externalTransaction.nonInterestBearingPrincipalReserve,
          ).toString()
        : '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    decimalAccrued: {
      id: 'decimalAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Decimal Accrued',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    dollarAccrued: {
      id: 'dollarAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Dollar Accrued',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    factor: {
      id: 'factor',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Factor',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    priceScalar: {
      id: 'priceScalar',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Price Scalar',
      value: '1.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    contractSize: {
      id: 'contractSize',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Contract Size',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    proceeds: {
      id: 'proceeds',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Proceeds',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    counterParty: {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'External Counter Party',
      value: '',
      custody: externalTransaction.counterParty ?? ' ',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    clearwaterCounterParty: {
      id: 'clearwaterCounterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Clearwater Counter Party',
      value: externalTransaction.clearwaterCounterParty?.name ?? 'Unknown',
      custody: externalTransaction.clearwaterCounterParty?.name ?? 'Unknown',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetDate: {
      id: 'amortTargetDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Amort Target Date',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetPrice: {
      id: 'amortTargetPrice',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Amort Target Price',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    inventoryType: {
      id: 'inventoryType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Inventory Type',
      value: externalTransaction.inventoryType ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    transTypeCode: {
      id: 'transTypeCode',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Code',
      value: externalTransaction.tranType ?? 'BUY',
      custody: externalTransaction.tranType ?? '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    buyType: {
      id: 'buyType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Buy Type',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawType: {
      id: 'drawType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Draw Type',
      value: 'Draw from Unfunded',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    reserveBalanceField: {
      id: 'reserveBalanceField',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Reserve Balance Field',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
  }
}

/**
 * Function to create Trade entry data from externalMatch
 */
export function createTradeEntryFromExternalTransactionTrading(
  externalTransaction: ExternalUnmatchedTransaction,
  accountName: string,
  isAggregate: boolean,
  hplMode: boolean,
): Record<TradeEntryRowName, TradeEntryModel> {
  return {
    account: {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: isAggregate,
      field: 'Account',
      value: accountName,
      trading: accountName,
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    transactionType: {
      id: 'transactionType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : 'Buy',
      trading: externalTransaction.tranType
        ? TransactionTypeEnum[externalTransaction.tranType]
        : 'Buy',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    dataSource: {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      trading: externalTransaction.dataSource ?? '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    entryDate: {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: externalTransaction.entryDate,
      trading: externalTransaction.entryDate,
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    settleDate: {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: externalTransaction.settleDate,
      trading: externalTransaction.settleDate,
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    postDate: {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: externalTransaction.postDate,
      trading: externalTransaction.postDate,
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    cusip: {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Asset ID',
      value: externalTransaction.assetId ?? '',
      trading: externalTransaction.assetId ?? '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    cwanIdentifier: {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: externalTransaction.cwanIdentifier ?? '',
      trading: externalTransaction.cwanIdentifier ?? '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    price: {
      id: 'price',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Price',
      value: externalTransaction.price
        ? Number(externalTransaction.price).toString()
        : '',
      trading: externalTransaction.price
        ? Number(externalTransaction.price).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    units: {
      id: 'units',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Units',
      value: '',
      trading: externalTransaction.units
        ? Number(externalTransaction.units).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    commission: {
      id: 'commission',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Commission',
      value: externalTransaction.commission
        ? Number(externalTransaction.commission).toString()
        : '0.0',
      trading: externalTransaction.commission
        ? Number(externalTransaction.commission).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    originationFee: {
      id: 'originationFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Origination Fee',
      value: externalTransaction.originationFee
        ? Number(externalTransaction.originationFee).toString()
        : '0.0',
      trading: externalTransaction.originationFee
        ? Number(externalTransaction.originationFee).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFee: {
      id: 'exitFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee',
      value: externalTransaction.exitFee
        ? Number(externalTransaction.exitFee).toString()
        : '0.0',
      trading: externalTransaction.exitFee
        ? Number(externalTransaction.exitFee).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFeeReceivable: {
      id: 'exitFeeReceivable',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee Receivable',
      value: externalTransaction.exitFeeReceivable
        ? Number(externalTransaction.exitFeeReceivable).toString()
        : '0.0',
      trading: externalTransaction.exitFeeReceivable
        ? Number(externalTransaction.exitFeeReceivable).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawFee: {
      id: 'drawFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Draw Fee',
      value: externalTransaction.drawFee
        ? Number(externalTransaction.drawFee).toString()
        : '0.0',
      trading: externalTransaction.drawFee
        ? Number(externalTransaction.drawFee).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaymentPenaltyFee: {
      id: 'prepaymentPenaltyFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Prepayment Penalty Fee',
      value: externalTransaction.prepaymentPenaltyFee
        ? Number(externalTransaction.prepaymentPenaltyFee).toString()
        : '0.0',
      trading: externalTransaction.prepaymentPenaltyFee
        ? Number(externalTransaction.prepaymentPenaltyFee).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaidInterest: {
      id: 'prepaidInterest',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Prepaid Interest',
      value: externalTransaction.prepaidInterest
        ? Number(externalTransaction.prepaidInterest).toString()
        : '0.0',
      trading: externalTransaction.prepaidInterest
        ? Number(externalTransaction.prepaidInterest).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestDue: {
      id: 'interestDue',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Past Due Interest',
      value: externalTransaction.interestDue
        ? Number(externalTransaction.interestDue).toString()
        : '0.0',
      trading: externalTransaction.interestDue
        ? Number(externalTransaction.interestDue).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingInterestReserve: {
      id: 'interestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Interest Bearing)',
      value: externalTransaction.interestBearingInterestReserve
        ? Number(externalTransaction.interestBearingInterestReserve).toString()
        : '0.0',
      trading: externalTransaction.interestBearingInterestReserve
        ? Number(externalTransaction.interestBearingInterestReserve).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingPrincipalReserve: {
      id: 'interestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Interest Bearing)',
      value: externalTransaction.interestBearingPrincipalReserve
        ? Number(externalTransaction.interestBearingPrincipalReserve).toString()
        : '0.0',
      trading: externalTransaction.interestBearingPrincipalReserve
        ? Number(externalTransaction.interestBearingPrincipalReserve).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingInterestReserve: {
      id: 'nonInterestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Non-Interest Bearing)',
      value: externalTransaction.nonInterestBearingInterestReserve
        ? Number(
            externalTransaction.nonInterestBearingInterestReserve,
          ).toString()
        : '0.0',
      trading: externalTransaction.nonInterestBearingInterestReserve
        ? Number(
            externalTransaction.nonInterestBearingInterestReserve,
          ).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingPrincipalReserve: {
      id: 'nonInterestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Non-Interest Bearing)',
      value: externalTransaction.nonInterestBearingPrincipalReserve
        ? Number(
            externalTransaction.nonInterestBearingPrincipalReserve,
          ).toString()
        : '0.0',
      trading: externalTransaction.nonInterestBearingPrincipalReserve
        ? Number(
            externalTransaction.nonInterestBearingPrincipalReserve,
          ).toString()
        : '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    decimalAccrued: {
      id: 'decimalAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Decimal Accrued',
      value: '',
      trading: '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    dollarAccrued: {
      id: 'dollarAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Dollar Accrued',
      value: externalTransaction.accruedAmount
        ? Number(externalTransaction.accruedAmount).toString()
        : '0.0',
      trading: externalTransaction.accruedAmount
        ? Number(externalTransaction.accruedAmount).toString()
        : '0.0',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    factor: {
      id: 'factor',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Factor',
      value: '',
      trading: '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    priceScalar: {
      id: 'priceScalar',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Price Scalar',
      value: '',
      trading: '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    contractSize: {
      id: 'contractSize',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Contract Size',
      value: '',
      trading: '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    proceeds: {
      id: 'proceeds',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Proceeds',
      value: '',
      trading: '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    counterParty: {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'External Counter Party',
      value: '',
      trading: externalTransaction.counterParty ?? 'Unknown',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    clearwaterCounterParty: {
      id: 'clearwaterCounterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Clearwater Counter Party',
      value: externalTransaction.clearwaterCounterParty?.name ?? 'Unknown',
      trading: externalTransaction.clearwaterCounterParty?.name ?? 'Unknown',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetDate: {
      id: 'amortTargetDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Amort Target Date',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetPrice: {
      id: 'amortTargetPrice',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Amort Target Price',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    inventoryType: {
      id: 'inventoryType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Inventory Type',
      value: externalTransaction.inventoryType ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    transTypeCode: {
      id: 'transTypeCode',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Code',
      value: externalTransaction.tranType ?? 'BUY',
      trading: externalTransaction.tranType ?? '',
      custody: '',
      errorMessage: '',
      thirdParty: '',
    },
    buyType: {
      id: 'buyType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Buy Type',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawType: {
      id: 'drawType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Draw Type',
      value: 'Draw from Unfunded',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    reserveBalanceField: {
      id: 'reserveBalanceField',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Reserve Balance Field',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
  }
}

/**
 * Function to create Trade entry data from HistoricalBalance
 */
export function createTradeEntryFromHistoricalBalance(
  pos: HistoricalBalanceData,
): Record<TradeEntryRowName, TradeEntryModel> {
  return {
    account: {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
    },
    transactionType: {
      id: 'transactionType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value:
        pos.unitDifference < 0
          ? TransactionTypeEnum['SELL']
          : TransactionTypeEnum['BUY'],
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    dataSource: {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    entryDate: {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: pos.date,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    settleDate: {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: pos.date,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    postDate: {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: pos.date,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    cusip: {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Asset ID',
      value: pos.assetId,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    cwanIdentifier: {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    price: {
      id: 'price',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Price',
      value: pos.price ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    units: {
      id: 'units',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Units',
      value: Number(pos.unitDifference).toString(),
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    commission: {
      id: 'commission',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Commission',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    originationFee: {
      id: 'originationFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Origination Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFee: {
      id: 'exitFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFeeReceivable: {
      id: 'exitFeeReceivable',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee Receivable',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawFee: {
      id: 'drawFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Draw Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaymentPenaltyFee: {
      id: 'prepaymentPenaltyFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Prepayment Penalty Fee',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaidInterest: {
      id: 'prepaidInterest',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Prepaid Interest',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestDue: {
      id: 'interestDue',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Past Due Interest',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingInterestReserve: {
      id: 'interestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingPrincipalReserve: {
      id: 'interestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingInterestReserve: {
      id: 'nonInterestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Non-Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingPrincipalReserve: {
      id: 'nonInterestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Non-Interest Bearing)',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    decimalAccrued: {
      id: 'decimalAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Decimal Accrued',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    dollarAccrued: {
      id: 'dollarAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Dollar Accrued',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    factor: {
      id: 'factor',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Factor',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    priceScalar: {
      id: 'priceScalar',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Price Scalar',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    contractSize: {
      id: 'contractSize',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Contract Size',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    proceeds: {
      id: 'proceeds',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Proceeds',
      value: '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    counterParty: {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'External Counter Party',
      value: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    clearwaterCounterParty: {
      id: 'clearwaterCounterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Clearwater Counter Party',
      value: 'Unknown',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetDate: {
      id: 'amortTargetDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Amort Target Date',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetPrice: {
      id: 'amortTargetPrice',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Amort Target Price',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    inventoryType: {
      id: 'inventoryType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Inventory Type',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    transTypeCode: {
      id: 'transTypeCode',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Code',
      value: pos.unitDifference < 0 ? 'SELL' : 'BUY',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    buyType: {
      id: 'buyType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Buy Type',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawType: {
      id: 'drawType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Draw Type',
      value: 'Draw from Unfunded',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    reserveBalanceField: {
      id: 'reserveBalanceField',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Reserve Balance Field',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
  }
}

/**
 * Turns a record of TradeEntryModels into a list for consumption by AG Grid
 */
export function tradeEntryRecordToArray(
  rec: Record<TradeEntryRowName, TradeEntryModel>,
): TradeEntryModel[] {
  return tradeEntryRowNames.map(name => rec[name])
}

/**
 * Function to edit Trade entry data
 */
export function editTradeEntryFromTransaction(
  trade: Trade,
  security: Security,
  accountName: string,
  combinedFactor: number,
  hplMode: boolean,
  accountNameAgg?: string,
  tradingAccountName?: string,
): Record<TradeEntryRowName, TradeEntryModel> {
  return {
    account: {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account',
      value: accountName,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    dataSource: {
      id: 'dataSource',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Data Source',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    transactionType: {
      id: 'transactionType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Transaction Type',
      value: TransactionTypeEnum[trade.tranType],
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    entryDate: {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: trade.entryDate,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    settleDate: {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: trade.settleDate,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    postDate: {
      id: 'postDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Post Date',
      value: trade.postDate,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    cusip: {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset ID',
      value: security.securityMasterData?.cusip,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    cwanIdentifier: {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: trade.cwanIdentifier ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    price: {
      id: 'price',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Price',
      value: trade.price ? Number(trade.price).toString() : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    units: {
      id: 'units',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Units',
      value: trade.units ? Number(trade.units).toString() : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    commission: {
      id: 'commission',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Commission',
      value: trade.commission ? Number(trade.commission).toString() : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    originationFee: {
      id: 'originationFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Origination Fee',
      value: trade.dataMap?.originationFee
        ? Number(trade.dataMap?.originationFee).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFee: {
      id: 'exitFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee',
      value: trade.dataMap?.exitFee
        ? Number(trade.dataMap?.exitFee).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    exitFeeReceivable: {
      id: 'exitFeeReceivable',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Exit Fee Receivable',
      value: trade.dataMap?.exitFeeReceivable
        ? Number(trade.dataMap?.exitFeeReceivable).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawFee: {
      id: 'drawFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Draw Fee',
      value: trade.dataMap?.drawFee
        ? Number(trade.dataMap?.drawFee).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaymentPenaltyFee: {
      id: 'prepaymentPenaltyFee',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Prepayment Penalty Fee',
      value: trade.dataMap?.prepaymentPenaltyFee
        ? Number(trade.dataMap?.prepaymentPenaltyFee).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    prepaidInterest: {
      id: 'prepaidInterest',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Prepaid Interest',
      value: trade.dataMap?.prepaidInterest
        ? Number(trade.dataMap?.prepaidInterest).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestDue: {
      id: 'interestDue',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Loan Past Due Interest',
      value: trade.dataMap?.interestDue
        ? Number(trade.dataMap?.interestDue).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingInterestReserve: {
      id: 'interestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Interest Bearing)',
      value: trade.dataMap?.interestBearingInterestReserve
        ? Number(trade.dataMap?.interestBearingInterestReserve).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    interestBearingPrincipalReserve: {
      id: 'interestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Interest Bearing)',
      value: trade.dataMap?.interestBearingPrincipalReserve
        ? Number(trade.dataMap?.interestBearingPrincipalReserve).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingInterestReserve: {
      id: 'nonInterestBearingInterestReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Interest Reserve (Non-Interest Bearing)',
      value: trade.dataMap?.nonInterestBearingInterestReserve
        ? Number(trade.dataMap?.nonInterestBearingInterestReserve).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    nonInterestBearingPrincipalReserve: {
      id: 'nonInterestBearingPrincipalReserve',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Principal Reserve (Non-Interest Bearing)',
      value: trade.dataMap?.nonInterestBearingPrincipalReserve
        ? Number(trade.dataMap?.nonInterestBearingPrincipalReserve).toString()
        : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    decimalAccrued: {
      id: 'decimalAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: !security.securityTypeData?.isFuture,
      field: 'Decimal Accrued',
      value: trade.accrued ? Number(trade.accrued).toString() : '0.0',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    dollarAccrued: {
      id: 'dollarAccrued',
      validityStatus: 'valid',
      type: 'number',
      isEditable: !security.securityTypeData?.isFuture,
      field: 'Dollar Accrued',
      value: Calculators.calculateAccruedAmount(
        security,
        trade.accrued ?? 0,
        trade.units ?? 0,
        trade.factor ?? 0,
      ).toString(),
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    factor: {
      id: 'factor',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Factor',
      value: trade.factor ? trade.factor.toString() : '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    priceScalar: {
      id: 'priceScalar',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Price Scalar',
      value: security.securityMasterData?.priceScalar.toString(),
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    contractSize: {
      id: 'contractSize',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Contract Size',
      value: security.securityMasterData?.contractSize
        ? security.securityMasterData?.contractSize.toString()
        : '1.0',
      custody: security.securityMasterData?.contractSize
        ? security.securityMasterData?.contractSize.toString()
        : '1.0',
      trading: security.securityMasterData?.contractSize
        ? security.securityMasterData?.contractSize.toString()
        : '1.0',
      errorMessage: '',
      thirdParty: '',
    },
    proceeds: {
      id: 'proceeds',
      validityStatus: 'valid',
      type: 'number',
      isEditable: false,
      field: 'Proceeds',
      value: Calculators.calculateProceeds(
        trade.price ?? 0,
        trade.accrued ?? 0,
        trade.units ?? 0,
        trade.commission,
        trade.dataMap?.originationFee ?? 0,
        trade.dataMap?.drawFee ?? 0,
        trade.dataMap?.exitFee ?? 0,
        trade.dataMap?.exitFeeReceivable ?? 0,
        trade.dataMap?.prepaymentPenaltyFee ?? 0,
        trade.dataMap?.prepaidInterest ?? 0,
        trade.dataMap?.interestDue ?? 0,
        (trade.dataMap?.interestBearingInterestReserve ?? 0) +
          (trade.dataMap?.interestBearingPrincipalReserve ?? 0) +
          (trade.dataMap?.nonInterestBearingInterestReserve ?? 0) +
          (trade.dataMap?.nonInterestBearingPrincipalReserve ?? 0),
        security,
        TransactionTypeEnum[trade.tranType],
        trade.factor ?? 0,
        hplMode,
        InventoryType[trade.inventoryType as keyof typeof InventoryType],
      ).toString(),
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    counterParty: {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'External Counter Party',
      value: '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    clearwaterCounterParty: {
      id: 'clearwaterCounterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Clearwater Counter Party',
      value: trade.counterParty?.name ?? 'Unknown',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetDate: {
      id: 'amortTargetDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Amort Target Date',
      value: trade.amortTargetDate ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    amortTargetPrice: {
      id: 'amortTargetPrice',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Amort Target Price',
      value: trade.amortTargetPrice
        ? Number(trade.amortTargetPrice).toString()
        : '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    inventoryType: {
      id: 'inventoryType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Inventory Type',
      value: trade.inventoryType ?? '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    transTypeCode: {
      id: 'transTypeCode',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Code',
      value: trade.tranType,
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    buyType: {
      id: 'buyType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Buy Type',
      value: trade.dataMap?.['paymentInKind'] == true ? 'Payment In Kind' : '',
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    drawType: {
      id: 'drawType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Draw Type',
      value: (() => {
        // A reserve balance is mutually exclusive with the draw-type options,
        // so leave Draw Type blank when one is set.
        if (trade.dataMap?.['reserveBalanceField']) return ''
        if (trade.dataMap?.['capitalizationOfInterest'])
          return 'Capitalization of Interest'
        if (trade.dataMap?.['interestReserveToFunded'])
          return 'Draw from Interest Reserve'
        return 'Draw from Unfunded'
      })(),
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
    reserveBalanceField: {
      id: 'reserveBalanceField',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Reserve Balance Field',
      value: reserveBalanceFieldLabelFromKey(
        trade.dataMap?.['reserveBalanceField'],
      ),
      custody: '',
      trading: '',
      errorMessage: '',
      thirdParty: '',
    },
  }
}

/**
 * Function to create transaction from custody external transaction
 */
export function createTradeEntryMatchFromTransactionMatch(
  externalTransaction: TransactionMatch,
  accountName: string,
  security: Security,
  counterPartyList: CounterParty[],
  calculatedUnits: number,
  combinedFactor: number,
): TradeEntryMatchRow {
  return {
    account: accountName,
    transactionType: externalTransaction.tranType
      ? TransactionTypeEnum[externalTransaction.tranType]
      : '',
    entryDate: externalTransaction.entryDate,
    settleDate: externalTransaction.settleDate,
    postDate: externalTransaction.postDate,
    cusip: security.securityMasterData.cusip,
    price: externalTransaction.price
      ? Number(externalTransaction.price).toString()
      : '',
    units: externalTransaction.units
      ? Number(externalTransaction.units).toString()
      : '',
    factor: combinedFactor.toString(),
    inventoryType: '',
    commission: externalTransaction.commission
      ? externalTransaction.commission.toString()
      : '',
    originationFee: externalTransaction.originationFee
      ? externalTransaction.originationFee.toString()
      : '',
    exitFee: externalTransaction.exitFee
      ? externalTransaction.exitFee.toString()
      : '',
    exitFeeReceivable: externalTransaction.exitFeeReceivable
      ? externalTransaction.exitFeeReceivable.toString()
      : '',
    drawFee: externalTransaction.drawFee
      ? externalTransaction.drawFee.toString()
      : '',
    prepaymentPenaltyFee: externalTransaction.prepaymentPenaltyFee
      ? externalTransaction.prepaymentPenaltyFee.toString()
      : '',
    prepaidInterest: externalTransaction.prepaidInterest
      ? externalTransaction.prepaidInterest.toString()
      : '',
    interestDue: externalTransaction.interestDue
      ? externalTransaction.interestDue.toString()
      : '',
    interestBearingInterestReserve:
      externalTransaction.interestBearingInterestReserve
        ? externalTransaction.interestBearingInterestReserve.toString()
        : '',
    interestBearingPrincipalReserve:
      externalTransaction.interestBearingPrincipalReserve
        ? externalTransaction.interestBearingPrincipalReserve.toString()
        : '',
    nonInterestBearingInterestReserve:
      externalTransaction.nonInterestBearingInterestReserve
        ? externalTransaction.nonInterestBearingInterestReserve.toString()
        : '',
    nonInterestBearingPrincipalReserve:
      externalTransaction.nonInterestBearingPrincipalReserve
        ? externalTransaction.nonInterestBearingPrincipalReserve.toString()
        : '',
    decimalAccrued: externalTransaction
      ? externalTransaction.accruedAmount
        ? Calculators.calculateAccruedInterest(
            security,
            externalTransaction.accruedAmount,
            calculatedUnits,
            combinedFactor,
            true,
          ).toString()
        : '0.0'
      : '',
    dollarAccrued: externalTransaction.accruedAmount
      ? Number(externalTransaction.accruedAmount).toString()
      : '',
    priceScalar: externalTransaction
      ? security.securityMasterData?.priceScalar.toString()
      : '',
    contractSize: security.securityMasterData?.contractSize
      ? security.securityMasterData?.contractSize.toString()
      : '1.0',
    proceeds: externalTransaction.amount
      ? externalTransaction.amount.toString()
      : '',
    counterParty: externalTransaction
      ? counterPartyList.length > 0 && externalTransaction.counterParty
        ? getCounterPartyFromName(
            externalTransaction.counterParty,
            counterPartyList,
          ).name
        : 'Unknown'
      : '',
    amortTargetDate: '',
    amortTargetPrice: '',
    buyType: '',
    drawType: '',
    reserveBalanceField: '',
  }
}

/**
 * Function to create Collateral fields
 */
export function getCollateralBroker(collateralValue?: string): TradeEntryModel {
  return {
    id: 'collateralBroker',
    validityStatus: 'valid',
    type: 'string',
    isEditable: true,
    field: 'Collateral Broker',
    value: collateralValue ?? '',
    custody: '',
    trading: '',
    errorMessage: '',
    thirdParty: '',
  }
}
